import { apiBaseOrigin } from '../../../shared/config/api-origin';
import { parseForecast, type Attention, type Evaluation, type Forecast } from './forecast-http';
import { createMonitoringRequest } from './http';

export interface EvidenceFrame {
  evaluation: Evaluation;
  forecasts: Forecast[];
}
export interface QuantifiedDetail {
  attention: Attention;
  asOf: string;
  trigger: EvidenceFrame | null;
  current: EvidenceFrame | null;
  series: EvidenceFrame[];
  reviewState: string;
  operator: '>=';
  logic: 'SINGLE_CLAUSE';
  statistic: 'SINGLE_FORECAST_PERIOD';
  windowMeaning: 'FORWARD_ELIGIBILITY_WINDOW';
  browserCanReview: false;
  limitations: string[];
}
export interface AuditPage {
  attentionId: string;
  items: Attention['audit'];
  nextVersion: number;
  hasMore: boolean;
}
const object = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const time = (v: unknown) => typeof v === 'string' && Number.isFinite(Date.parse(v));
export function parseQuantified(v: unknown, attention: Attention): QuantifiedDetail {
  if (
    !object(v) ||
    !object(v.attention) ||
    v.attention.id !== attention.id ||
    v.attention.version !== attention.version ||
    v.attention.evaluationId !== attention.evaluationId ||
    v.attention.inputMode !== 'DEMO' ||
    attention.inputMode !== 'DEMO' ||
    v.attention.regionId !== attention.regionId ||
    !time(v.asOf) ||
    !Array.isArray(v.series) ||
    v.series.length > 64 ||
    v.operator !== '>=' ||
    v.logic !== 'SINGLE_CLAUSE' ||
    v.statistic !== 'SINGLE_FORECAST_PERIOD' ||
    v.windowMeaning !== 'FORWARD_ELIGIBILITY_WINDOW' ||
    v.browserCanReview !== false ||
    !['PENDING', 'ACKNOWLEDGED', 'REVIEWED', 'DISMISSED'].includes(String(v.reviewState)) ||
    !Array.isArray(v.limitations) ||
    !v.limitations.every((x) => typeof x === 'string')
  )
    throw new Error('EVIDENCE_IDENTITY_CONFLICT');
  const a = v.attention;
  for (const frame of [v.trigger, v.current, ...v.series]) {
    if (frame === null) continue;
    if (
      !object(frame) ||
      !object(frame.evaluation) ||
      !Array.isArray(frame.forecasts) ||
      frame.forecasts.length > 24
    )
      throw new Error('EVIDENCE_FRAME');
    const e = frame.evaluation;
    parseForecast(
      {
        sourceMode: 'DEMO',
        generatedAt: v.asOf,
        forecasts: frame.forecasts,
        evaluations: [e],
        attentions: [a],
        rules: [],
        health: [],
        officialAlerts: 'NOT_CONNECTED',
        limitations: [],
      },
      'DEMO',
    );
    if (
      e.regionId !== a.regionId ||
      e.ruleId !== a.ruleId ||
      e.ruleVersion !== a.ruleVersion ||
      Date.parse(String(e.evaluatedAt)) > Date.parse(String(v.asOf))
    )
      throw new Error('EVIDENCE_SCOPE_CONFLICT');
    if (
      e.metrics != null &&
      (!Array.isArray(e.metrics) ||
        !e.metrics.every(
          (m) =>
            object(m) &&
            typeof m.name === 'string' &&
            typeof m.unit === 'string' &&
            typeof m.quality === 'string' &&
            (m.value === null || (typeof m.value === 'number' && Number.isFinite(m.value))),
        ))
    )
      throw new Error('EVIDENCE_METRICS');
    const r = e.thresholdsFromApprovedRule;
    if (
      r != null &&
      (!object(r) ||
        r.id !== e.ruleId ||
        r.version !== e.ruleVersion ||
        r.regionId !== e.regionId ||
        r.sourceMode !== e.inputMode ||
        r.approvalState !== 'DEMO_APPROVED' ||
        r.enabled !== true ||
        typeof r.metric !== 'string' ||
        typeof r.unit !== 'string' ||
        typeof r.minimum !== 'number' ||
        !Number.isFinite(r.minimum) ||
        !time(r.validFrom) ||
        !time(r.validUntil) ||
        typeof r.maxAgeSeconds !== 'number' ||
        r.maxAgeSeconds <= 0 ||
        typeof r.windowSeconds !== 'number' ||
        r.windowSeconds < 0)
    )
      throw new Error('EVIDENCE_RULE_CONFLICT');
    const ids = new Set<string>();
    for (const raw of frame.forecasts) {
      const f = raw as Forecast;
      if (
        ids.has(f.id) ||
        !(e.forecastIds as string[]).includes(f.id) ||
        f.region.regionId !== e.regionId ||
        f.region.instanceId !== e.instanceId ||
        Date.parse(f.issuedAt) > Date.parse(String(e.evaluatedAt)) ||
        Date.parse(f.retrievedAt) > Date.parse(String(e.evaluatedAt))
      )
        throw new Error('EVIDENCE_INPUT_CONFLICT');
      ids.add(f.id);
    }
  }
  const d = v as unknown as QuantifiedDetail;
  if (d.current && d.current.evaluation.id !== attention.evaluationId)
    throw new Error('EVIDENCE_VERSION_CONFLICT');
  if (
    d.trigger &&
    d.current &&
    Date.parse(d.current.evaluation.evaluatedAt) < Date.parse(d.trigger.evaluation.evaluatedAt)
  )
    throw new Error('EVIDENCE_TIME_CONFLICT');
  return d;
}
const path = '/read-models/normal-monitoring/weather-attention';
export function readQuantified(a: Attention, signal: AbortSignal) {
  return createMonitoringRequest({ baseOrigin: apiBaseOrigin() })(
    `${path}?detailId=${encodeURIComponent(a.id)}&version=${a.version}`,
    (v) => parseQuantified(v, a),
    signal,
  );
}
export function parseAudit(v: unknown, id: string, after: number): AuditPage {
  if (
    !object(v) ||
    v.attentionId !== id ||
    !Array.isArray(v.items) ||
    v.items.length > 100 ||
    typeof v.hasMore !== 'boolean' ||
    !Number.isSafeInteger(v.nextVersion)
  )
    throw new Error('AUDIT_PAGE');
  let previous = after;
  for (const r of v.items) {
    if (
      !object(r) ||
      !Number.isSafeInteger(r.version) ||
      Number(r.version) <= previous ||
      !time(r.at) ||
      !['actor', 'action', 'reason', 'evaluationId'].every((k) => typeof r[k] === 'string')
    )
      throw new Error('AUDIT_ORDER');
    previous = Number(r.version);
  }
  if (v.nextVersion !== previous || (v.hasMore && previous === after))
    throw new Error('AUDIT_CURSOR');
  return v as unknown as AuditPage;
}
export function readAudit(id: string, after: number, signal: AbortSignal) {
  return createMonitoringRequest({ baseOrigin: apiBaseOrigin() })(
    `${path}?attentionId=${encodeURIComponent(id)}&afterVersion=${after}`,
    (v) => parseAudit(v, id, after),
    signal,
  );
}
